import { HandleErrorAndLoading } from "@common/HandleErrorAndLoading/HandleErrorAndLoading";
import { useStyle } from "./style";
import { useTranslation } from "react-i18next";
import { Controller, useFormContext } from "react-hook-form";
import esimLogo from "@static/icons/Esim.png";
import { Fragment } from "react";
import { cls, IFetchState } from "itsf-ui-common";
import { IDeviceDetailsResponse } from "@features/cart/interfaces/IDeviceDetails";
import { eidRegex } from "@utils/regex";
import { isEidValid } from "@utils/eidUtils";
import { useGetDeviceList } from "@features/cart/hook/useGetDeviceList";
import { IDeviceBrandValueLabel, IESimDeviceForm } from "@features/cart/interfaces/IESimDeviceForm";
import { Grid, MenuItem, Stack, Tooltip, Typography } from "@mui/material";
import { Help as HelpIcon } from "@mui/icons-material";
import { LabeledTextField } from "@common/LabeledTextField/LabeledTextField";
import { PLACEHOLDER_OPACITY } from "../../../../theme/themeConfiguration";
import { LabeledButton } from "@common/LabeledButton/LabeledButton";
import { useDeviceType } from "@hooks/useDeviceType";
import { getObjectByPath } from "@utils/getObjectByPath";
import type { FieldError } from "react-hook-form";
import { useESimDeviceForm } from "@features/cart/hook/useESimDeviceForm";

interface IParams {
    offerId: number;
    setIsESimFormOpen: (isESimFormOpen: boolean) => void;
    deviceDetails: IDeviceDetailsResponse | undefined;
    deviceDetailsState: IFetchState;
    postDeviceDetails: (offerId: number, formData: IESimDeviceForm) => Promise<unknown>;
    postDeviceDetailsState: IFetchState;
    deleteDeviceDetails: (offerId: number) => Promise<unknown>;
    deleteDeviceDetailsState: IFetchState;
    postInclusiveEquipmentState: IFetchState;
}

export const ESimDeviceForm = (props: IParams) => {
    const {
        setIsESimFormOpen,
        offerId,
        deleteDeviceDetails,
        deleteDeviceDetailsState,
        postInclusiveEquipmentState,
        deviceDetails,
        deviceDetailsState,
        postDeviceDetails,
        postDeviceDetailsState,
    } = props;

    const { t } = useTranslation(["eSim", "common", "validation"]);
    const { classes } = useStyle();

    const { isMobile } = useDeviceType();

    const otherChoice: IDeviceBrandValueLabel = {
        value: t("eSim:other"),
        label: t("eSim:other"),
    };

    const { deviceList, getDeviceListState } = useGetDeviceList();
    const { clearErrors, control, errors } = useFormContext();
    const { brandFieldName, eidFieldName, isBrandSelected, isOtherChoice, onCancel, onSubmit } = useESimDeviceForm({
        deleteDeviceDetails,
        deviceDetails,
        offerId,
        otherChoice,
        postDeviceDetails,
        setIsESimFormOpen,
    });

    const mapDeviceListLabelFormat = (): IDeviceBrandValueLabel[] => {
        if (deviceList) {
            return deviceList.manufacturers.map((device) => ({
                label: device.charAt(0) + device.slice(1).toLowerCase(),
                value: device,
            }));
        }

        return [];
    };
    const menuDeviceListItems = [...mapDeviceListLabelFormat(), otherChoice];

    const brandFieldError = getObjectByPath<FieldError>(errors, brandFieldName);
    const eidFieldError = getObjectByPath<FieldError>(errors, eidFieldName);
    const eidFormErrorMessage = eidFieldError?.message ?? "";

    const error = deviceDetailsState.error ?? postDeviceDetailsState.error ?? postInclusiveEquipmentState.error;
    const isLoading =
        deviceDetailsState.isLoading ||
        postDeviceDetailsState.isLoading ||
        deleteDeviceDetailsState.isLoading ||
        postInclusiveEquipmentState.isLoading;

    const validateEid = (value: string) => {
        if (!isBrandSelected) {
            return true;
        }

        if (!value) {
            return t("validation:required");
        }

        if (!eidRegex.test(value)) {
            return t("eSim:invalidEidLength");
        }

        if (!isEidValid(value)) {
            return t("eSim:invalidEidFormat");
        }

        return true;
    };

    return (
        <div className={classes.container}>
            <HandleErrorAndLoading error={error} isLoading={isLoading}>
                <form className={`xtest_portin_form ${classes.form}`} onSubmit={onSubmit}>
                    <Grid container direction="column" spacing={2}>
                        <Grid item>
                            <Grid alignItems="flex-end" container spacing={1}>
                                <Grid item>
                                    <Typography color="secondary" variant="h4">
                                        {t("eSim:phoneDetails")}
                                    </Typography>
                                </Grid>
                                <Grid item>
                                    <Typography color="secondary" variant="subtitle1">
                                        {t("eSim:optional")}
                                    </Typography>
                                </Grid>
                            </Grid>
                        </Grid>
                        <Grid item>
                            <Typography color="secondary" variant="normal">
                                {t("eSim:eachBrand")}
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Grid alignItems="flex-start" container spacing={2}>
                                <Grid item md={6} xs={12}>
                                    <Controller
                                        control={control}
                                        defaultValue=""
                                        name={brandFieldName}
                                        render={(fieldProps) => (
                                            <LabeledTextField
                                                {...fieldProps}
                                                SelectProps={{ displayEmpty: true }}
                                                error={
                                                    Boolean(brandFieldError) || getDeviceListState.error !== undefined
                                                }
                                                fullWidth
                                                helperText={
                                                    brandFieldError?.message ?? getDeviceListState.error?.errorMessage
                                                }
                                                id="brand"
                                                labelText={t("eSim:brand")}
                                                name={brandFieldName}
                                                select
                                                onChange={(event) => {
                                                    clearErrors(brandFieldName);
                                                    fieldProps.onChange(event);
                                                }}
                                            >
                                                <MenuItem disabled value="">
                                                    <Typography sx={{ opacity: PLACEHOLDER_OPACITY }}>
                                                        {t("eSim:enterBrand")}
                                                    </Typography>
                                                </MenuItem>
                                                {menuDeviceListItems.map((option) => (
                                                    <MenuItem key={option.value} value={option.value}>
                                                        {option.label}
                                                    </MenuItem>
                                                ))}
                                            </LabeledTextField>
                                        )}
                                    />
                                </Grid>
                                {isBrandSelected && (
                                    <Fragment>
                                        <Grid item md={5} xs={11}>
                                            <Controller
                                                control={control}
                                                defaultValue=""
                                                name={eidFieldName}
                                                render={(fieldProps) => (
                                                    <LabeledTextField
                                                        {...fieldProps}
                                                        error={Boolean(eidFormErrorMessage)}
                                                        fullWidth
                                                        helperText={eidFormErrorMessage}
                                                        id="eid"
                                                        isRequired
                                                        labelText={t("eSim:eid")}
                                                        name={eidFieldName}
                                                        onChange={(event) => {
                                                            clearErrors(eidFieldName);
                                                            fieldProps.onChange(event);
                                                        }}
                                                    />
                                                )}
                                                rules={{
                                                    validate: validateEid,
                                                }}
                                            />
                                        </Grid>
                                        <Grid className={classes.eSimHelpIcon} item md={1} xs={1}>
                                            <Tooltip
                                                color="secondary"
                                                placement="top-end"
                                                title={t("eSim:eidDescription")}
                                            >
                                                <HelpIcon />
                                            </Tooltip>
                                        </Grid>
                                    </Fragment>
                                )}
                            </Grid>
                        </Grid>
                        {isOtherChoice && (
                            <Grid item>
                                <div className={classes.eSimOtherDescription}>
                                    <Grid
                                        alignItems="center"
                                        container
                                        justifyContent="flex-start"
                                        spacing={2}
                                        wrap="nowrap"
                                    >
                                        <Grid className={classes.eSimIcon} item>
                                            <img alt={"eSIM Logo"} src={esimLogo} />
                                        </Grid>
                                        <Grid item md={11} sm={11}>
                                            <Typography color="primary" variant="normal">
                                                {t("eSim:otherDeviceDescription")}
                                            </Typography>
                                        </Grid>
                                    </Grid>
                                </div>
                            </Grid>
                        )}
                        <Grid className={classes.eSimSubmitGridItem} item>
                            <Stack
                                alignItems="center"
                                className={classes.eSimSubmitStack}
                                flexDirection={isMobile ? "column-reverse" : "row"}
                                gap={2}
                            >
                                <LabeledButton
                                    className={cls("xtest_cancel_button", classes.cancelButton)}
                                    color="primary"
                                    id="cancelButton"
                                    labelText={t("common:cancel")}
                                    variant="text"
                                    onClick={onCancel}
                                >
                                    {t("common:cancel")}
                                </LabeledButton>
                                <LabeledButton
                                    className="xtest_submit_button"
                                    color="primary"
                                    id="submitButton"
                                    labelText={t("common:continue")}
                                    type="submit"
                                >
                                    {t("common:continue")}
                                </LabeledButton>
                            </Stack>
                        </Grid>
                    </Grid>
                </form>
            </HandleErrorAndLoading>
        </div>
    );
};
